import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { addToQueue } from '../ark/utils/queueStorage';
import './jobs.css';

const ROLE_CATEGORIES = [
  'Software Engineering',
  'Data & Analytics',
  'Product',
  'Sales',
  'Marketing',
  'Operations',
  'Finance & Accounting',
  'Legal',
  'HR & Recruiting',
  'Design',
];

const ROLE_KEYWORDS = {
  'Software Engineering': ['engineer', 'developer', 'swe', 'backend', 'frontend', 'fullstack', 'full-stack', 'infrastructure', 'devops', 'platform', 'mobile', 'ios', 'android', 'firmware', 'embedded', 'software'],
  'Data & Analytics': ['data', 'analyst', 'analytics', 'scientist', 'ml', 'machine learning', 'ai ', 'research', 'quantitative', 'quant', 'intelligence', 'insights', 'bi '],
  'Product': ['product manager', 'product lead', 'pm ', 'product owner', 'product design', 'ux', 'ui ', 'user experience', 'product strategy'],
  'Sales': ['sales', 'account executive', 'ae ', 'business development', 'bdr', 'sdr', 'revenue', 'partnerships', 'account manager', 'customer success', 'client'],
  'Marketing': ['marketing', 'growth', 'brand', 'content', 'social media', 'seo', 'demand gen', 'communications', 'pr ', 'public relations', 'copywriter'],
  'Operations': ['operations', 'ops ', 'program manager', 'project manager', 'strategy', 'chief of staff', 'supply chain', 'logistics', 'process'],
  'Finance & Accounting': ['finance', 'financial', 'accounting', 'accountant', 'controller', 'cfo', 'treasury', 'tax', 'audit', 'fp&a', 'investment'],
  'Legal': ['legal', 'counsel', 'attorney', 'compliance', 'privacy', 'regulatory', 'policy'],
  'HR & Recruiting': ['recruiter', 'recruiting', 'talent', 'people ops', 'hr ', 'human resources', 'compensation', 'benefits', 'learning'],
  'Design': ['design', 'designer', 'creative', 'visual', 'motion', 'illustrat'],
};

const WORK_MODE_LABELS = { remote: 'Remote', hybrid: 'Hybrid', onsite: 'On-site', unspecified: 'On-site' };

function inferWorkMode(job) {
  if (job.work_mode && job.work_mode !== 'unspecified') return job.work_mode;
  const t = job.job_title.toLowerCase();
  if (/\bremote\b/.test(t)) return 'remote';
  if (/\bhybrid\b/.test(t)) return 'hybrid';
  if (/\bon-?site\b/.test(t)) return 'onsite';
  return 'unspecified';
}

function cleanTitle(title) {
  let t = title.split(/[•·]/)[0].trim();
  // Strip "APPLY → ..." suffix from html_generic scrapes
  t = t.replace(/\s+APPLY\b.*/i, '').trim();
  // Strip trailing work mode + optional location (e.g. "Remote", "Hybrid- Pittsburgh, PA", "Onsite- Salem, OR")
  t = t.replace(/\s+(Remote|Hybrid|On-?site)(\s*[-–,/]\s*.+)?$/i, '').trim();
  return t;
}

function matchesRole(jobTitle, roleCategory) {
  const title = jobTitle.toLowerCase();
  const keywords = ROLE_KEYWORDS[roleCategory] || [];
  return keywords.some(kw => title.includes(kw));
}

function toggleItem(arr, item) {
  return arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item];
}

function FilterBtn({ label, count, isOpen, onToggle, children, hasSelection }) {
  return (
    <div className="jobs-filter-group">
      <button
        className={`jobs-filter-btn ${hasSelection ? 'has-selection' : ''}`}
        onClick={onToggle}
      >
        {label}
        {count > 0 && <span className="jobs-filter-count">{count}</span>}
        <span className="jobs-filter-caret">{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen && <div className="jobs-dropdown">{children}</div>}
    </div>
  );
}

function jobKey(job) {
  return job.job_url || `${job.company_name}__${job.job_title}`;
}

export default function OpenRolesPage({ selectedIndustries, setSelectedIndustries, selectedRoles, setSelectedRoles }) {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [allCompanies, setAllCompanies] = useState([]);
  const [allIndustries, setAllIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedWorkModes, setSelectedWorkModes] = useState([]);
  const [offset, setOffset] = useState(0);

  // Selection (Gmail-style)
  const [selectedJobs, setSelectedJobs] = useState(new Set());

  // Dropdown open state
  const [openDropdown, setOpenDropdown] = useState(null); // 'industry'|'role'|'company'|'workmode'

  const LIMIT = 60;
  const searchRef = useRef(null);

  const allJobKeys = jobs.map(jobKey);
  const allSelected = allJobKeys.length > 0 && allJobKeys.every(k => selectedJobs.has(k));
  const someSelected = !allSelected && allJobKeys.some(k => selectedJobs.has(k));

  const toggleJob = (key) => {
    setSelectedJobs(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected || someSelected) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(allJobKeys));
    }
  };

  const clearSelection = () => setSelectedJobs(new Set());
  const navigate = useNavigate();

  const handleSendToArk = () => {
    const selected = jobs.filter(j => selectedJobs.has(jobKey(j)));
    const payload = selected.map(j => ({
      _key: jobKey(j),
      job_title: j.job_title,
      company_name: j.company_name,
      job_url: j.job_url || null,
      work_mode: j.work_mode,
      industry: j.industry,
    }));
    addToQueue(payload);
    clearSelection();
    navigate('/jobs/ark');
  };

  const fetchJobs = useCallback(async (off = 0) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: LIMIT, offset: off });
      if (search) params.set('search', search);
      if (selectedCompanies.length) params.set('company', selectedCompanies[0]); // single company filter
      if (selectedIndustries.length) params.set('industry', selectedIndustries.join(','));
      if (selectedWorkModes.length) params.set('work_mode', selectedWorkModes.join(','));

      const res = await fetch(`/api/jobs?${params}`);
      if (!res.ok) throw new Error('Failed to fetch jobs');
      const data = await res.json();

      // Client-side role filter (keyword matching)
      let filtered = data.jobs;
      if (selectedRoles.length > 0) {
        filtered = filtered.filter(j => selectedRoles.some(r => matchesRole(j.job_title, r)));
      }

      if (off === 0) {
        setJobs(filtered);
      } else {
        setJobs(prev => [...prev, ...filtered]);
      }
      setTotal(data.total);
      setTotalCompanies(data.totalCompanies || 0);
      if (off === 0) {
        setAllCompanies(data.companies || []);
        setAllIndustries(data.industries || []);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [search, selectedCompanies, selectedIndustries, selectedWorkModes, selectedRoles]);

  // Refetch on filter change
  useEffect(() => {
    setOffset(0);
    fetchJobs(0);
  }, [fetchJobs]);

  const loadMore = () => {
    const next = offset + LIMIT;
    setOffset(next);
    fetchJobs(next);
  };

  const clearAll = () => {
    setSearch('');
    setSelectedIndustries([]);
    setSelectedRoles([]);
    setSelectedCompanies([]);
    setSelectedWorkModes([]);
    if (searchRef.current) searchRef.current.value = '';
  };

  const activeCount =
    selectedIndustries.length + selectedRoles.length +
    selectedCompanies.length + selectedWorkModes.length +
    (search ? 1 : 0);

  // Group jobs by company for display
  const grouped = jobs.reduce((acc, job) => {
    if (!acc[job.company_name]) acc[job.company_name] = { careers_url: job.careers_url, industry: job.industry, jobs: [] };
    acc[job.company_name].jobs.push(job);
    return acc;
  }, {});

  const closeDropdowns = () => setOpenDropdown(null);

  return (
    <div className="jobs-panel">
      <header className="jobs-page-topbar">
        <span className="jobs-page-title">Open Roles</span>
      </header>

      <div className="jobs-page">
      {/* ── Filter bar ── */}
      <div className="jobs-filters">

        {/* Search */}
        <div className="or-search-wrap">
          <input
            ref={searchRef}
            type="text"
            className="or-search"
            placeholder="Search job title or company..."
            defaultValue={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Industry */}
        <FilterBtn
          label="Industry"
          count={selectedIndustries.length}
          isOpen={openDropdown === 'industry'}
          onToggle={() => setOpenDropdown(o => o === 'industry' ? null : 'industry')}
          hasSelection={selectedIndustries.length > 0}
        >
          {allIndustries.map(ind => (
            <label key={ind} className="jobs-dropdown-item">
              <input
                type="checkbox"
                checked={selectedIndustries.includes(ind)}
                onChange={() => { setSelectedIndustries(prev => toggleItem(prev, ind)); }}
              />
              {ind}
            </label>
          ))}
        </FilterBtn>

        {/* Role */}
        <FilterBtn
          label="Role"
          count={selectedRoles.length}
          isOpen={openDropdown === 'role'}
          onToggle={() => setOpenDropdown(o => o === 'role' ? null : 'role')}
          hasSelection={selectedRoles.length > 0}
        >
          {ROLE_CATEGORIES.map(role => (
            <label key={role} className="jobs-dropdown-item">
              <input
                type="checkbox"
                checked={selectedRoles.includes(role)}
                onChange={() => setSelectedRoles(prev => toggleItem(prev, role))}
              />
              {role}
            </label>
          ))}
        </FilterBtn>

        {/* Company */}
        <FilterBtn
          label="Company"
          count={selectedCompanies.length}
          isOpen={openDropdown === 'company'}
          onToggle={() => setOpenDropdown(o => o === 'company' ? null : 'company')}
          hasSelection={selectedCompanies.length > 0}
        >
          <div className="or-company-search-wrap">
            <input
              type="text"
              className="or-dropdown-search"
              placeholder="Filter companies..."
              onClick={e => e.stopPropagation()}
              onChange={e => {
                const q = e.target.value.toLowerCase();
                const items = document.querySelectorAll('.or-company-item');
                items.forEach(el => {
                  el.style.display = el.dataset.name.includes(q) ? '' : 'none';
                });
              }}
            />
          </div>
          {allCompanies.map(co => (
            <label key={co} className="jobs-dropdown-item or-company-item" data-name={co.toLowerCase()}>
              <input
                type="checkbox"
                checked={selectedCompanies.includes(co)}
                onChange={() => setSelectedCompanies(prev => toggleItem(prev, co))}
              />
              {co}
            </label>
          ))}
        </FilterBtn>

        {/* Work mode */}
        <FilterBtn
          label="Work Mode"
          count={selectedWorkModes.length}
          isOpen={openDropdown === 'workmode'}
          onToggle={() => setOpenDropdown(o => o === 'workmode' ? null : 'workmode')}
          hasSelection={selectedWorkModes.length > 0}
        >
          {Object.entries(WORK_MODE_LABELS).map(([val, label]) => (
            <label key={val} className="jobs-dropdown-item">
              <input
                type="checkbox"
                checked={selectedWorkModes.includes(val)}
                onChange={() => setSelectedWorkModes(prev => toggleItem(prev, val))}
              />
              {label}
            </label>
          ))}
        </FilterBtn>

        {/* Active chips */}
        {activeCount > 0 && (
          <div className="jobs-chips">
            {search && (
              <span className="jobs-chip role">
                "{search}"
                <button onClick={() => { setSearch(''); if (searchRef.current) searchRef.current.value = ''; }}>×</button>
              </span>
            )}
            {selectedIndustries.map(ind => (
              <span key={ind} className="jobs-chip industry">
                {ind}
                <button onClick={() => setSelectedIndustries(p => p.filter(x => x !== ind))}>×</button>
              </span>
            ))}
            {selectedRoles.map(r => (
              <span key={r} className="jobs-chip role">
                {r}
                <button onClick={() => setSelectedRoles(p => p.filter(x => x !== r))}>×</button>
              </span>
            ))}
            {selectedCompanies.map(co => (
              <span key={co} className="jobs-chip company">
                {co}
                <button onClick={() => setSelectedCompanies(p => p.filter(x => x !== co))}>×</button>
              </span>
            ))}
            {selectedWorkModes.map(m => (
              <span key={m} className="jobs-chip workmode">
                {WORK_MODE_LABELS[m]}
                <button onClick={() => setSelectedWorkModes(p => p.filter(x => x !== m))}>×</button>
              </span>
            ))}
            <button className="jobs-clear-btn" onClick={clearAll}>Clear all</button>
          </div>
        )}
      </div>

      {openDropdown && (
        <div className="jobs-overlay" onClick={closeDropdowns} />
      )}

      {/* ── Results ── */}
      <main className="jobs-content">

        {/* Gmail-style toolbar */}
        <div className="or-toolbar">
          <div className="or-toolbar-left">
            <label className="or-select-all-wrap" title={allSelected ? 'Deselect all' : 'Select all'}>
              <input
                type="checkbox"
                className="or-select-all-box"
                checked={allSelected}
                ref={el => { if (el) el.indeterminate = someSelected; }}
                onChange={toggleSelectAll}
              />
              <span className="or-select-all-caret" onClick={e => { e.preventDefault(); toggleSelectAll(); }}>▾</span>
            </label>
          </div>
          <div className="jobs-count" style={{ margin: 0 }}>
            {loading && jobs.length === 0
              ? 'Loading...'
              : `${total.toLocaleString()} roles${activeCount > 0 ? ' matching filters' : ''} · ${totalCompanies} companies`
            }
          </div>
        </div>

        {error && <div className="jobs-empty">Error loading jobs: {error}</div>}

        {!loading && jobs.length === 0 && !error && (
          <div className="jobs-empty">No roles match the selected filters.</div>
        )}

        {/* Grouped by company */}
        <div className="or-companies">
          {Object.entries(grouped).map(([company, data]) => (
            <div key={company} className="or-company-block">
              <div className="or-company-header">
                <div className="or-company-header-left">
                  <span className="or-company-name">{company}</span>
                  <span className="or-company-count">{data.jobs.length} role{data.jobs.length !== 1 ? 's' : ''}</span>
                  {data.industry && (
                    <span className="jobs-tag industry">{data.industry}</span>
                  )}
                </div>
                <a
                  href={data.careers_url}
                  className="jobs-careers-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Careers page →
                </a>
              </div>

              <ul className="or-jobs-list">
                {data.jobs.map((job, i) => {
                  const key = jobKey(job);
                  const isChecked = selectedJobs.has(key);
                  return (
                    <li key={i} className={`or-job-row${isChecked ? ' selected' : ''}`}>
                      <label className="or-job-checkbox-wrap" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="or-job-checkbox"
                          checked={isChecked}
                          onChange={() => toggleJob(key)}
                        />
                      </label>
                      <div className="or-job-left">
                        {job.job_url ? (
                          <a
                            href={job.job_url}
                            className="or-job-title"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {cleanTitle(job.job_title)}
                          </a>
                        ) : (
                          <span className="or-job-title no-link">{cleanTitle(job.job_title)}</span>
                        )}
                        {job.location && (
                          <span className="or-job-location">{job.location}</span>
                        )}
                      </div>
                      <div className="or-job-right">
                        {(() => { const wm = inferWorkMode(job); return (
                          <span className={`or-work-mode ${wm}`}>
                            {WORK_MODE_LABELS[wm]}
                          </span>
                        ); })()}
                        {job.job_url && (
                          <a
                            href={job.job_url}
                            className="or-apply-link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Apply →
                          </a>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* Load more */}
        {jobs.length < total && (
          <div className="or-load-more">
            <button
              className="or-load-more-btn"
              onClick={loadMore}
              disabled={loading}
            >
              {loading ? 'Loading...' : `Load more (${total - jobs.length} remaining)`}
            </button>
          </div>
        )}
      </main>

      {/* Floating selection bar */}
      {selectedJobs.size > 0 && (
        <div className="or-selection-bar">
          <span className="or-selection-count">
            {selectedJobs.size} role{selectedJobs.size !== 1 ? 's' : ''} selected
          </span>
          <button className="or-selection-ark-btn" onClick={handleSendToArk}>
            Send to ark →
          </button>
          <button className="or-selection-clear" onClick={clearSelection}>
            Clear
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
